import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  IconButton,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import InfoIcon from "@mui/icons-material/Info";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

export const AddCustomVideoDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { token, user } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);

  const [selectedPlatform, setSelectedPlatform] = useState("YouTube");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handlePlatformChange = (event) => {
    setSelectedPlatform(event.target.value);
  };

  const onSubmit = async (data) => {
    if (!data.name || !data.id) {
      enqueueSnackbar("Please fill out all fields.", { variant: "error" });
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/video`,
        {
          displayname: selectedPlatform + " - " + data.name,
          videolink: data.id,
          vimeolink: selectedPlatform,
          randomkey: 0,
          practiceid: user?.practiceId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      enqueueSnackbar("Video added successfully!", { variant: "success" });
      onClose();
    } catch (err) {
      console.error("Error fetching videos:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            color: darkMode
              ? getColors.thumbnailTextDark
              : getColors.secondaryTextLight,
          }}
        >
          Add YouTube or Vimeo Video
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Video Name Field */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "18px",
              }}
            >
              Video Name
            </Typography>
            <Tooltip title="Enter the name to identify this video in your course.">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="e.g., Introduction to Marketing"
            {...register("name", { required: "Video name is required" })}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Box>

        {/* Video Link Field */}
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                fontSize: "18px",
                color: darkMode ? "#fff" : "#333",
              }}
            >
              Video Link
            </Typography>
            <Tooltip title="Paste the YouTube or Vimeo video link here.">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              selectedPlatform === "YouTube"
                ? "e.g., dQw4w9WgXcQ"
                : "e.g., 123456789"
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      paddingLeft: "8px",
                      color: "gray",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedPlatform === "YouTube"
                      ? "https://youtu.be/"
                      : "https://vimeo.com/"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: darkMode ? "#2f2f2f" : "#f5faff", // softer highlight
              borderRadius: 1,
              transition: "background-color 0.3s ease",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: darkMode ? "#444" : "#ccc",
                },
                "&:hover fieldset": {
                  borderColor: darkMode ? "#888" : "#666",
                },
                "&.Mui-focused": {
                  backgroundColor: darkMode ? "#3a3a3a" : "#e3f2fd", // light highlight on focus
                  "& fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              },
            }}
            {...register("id", {
              required: "Video ID is required",
              onChange: (e) => {
                const value = e.target.value;
                let id = value;

                if (selectedPlatform === "YouTube") {
                  const match = value.match(/(?:v=|\/)([a-zA-Z0-9_-]{11})/);
                  if (match) id = match[1];
                } else if (selectedPlatform === "Vimeo") {
                  const match = value.match(/vimeo\.com\/(\d+)/);
                  if (match) id = match[1];
                }

                e.target.value = id;
              },
            })}
            error={!!errors.id}
            helperText={errors.id?.message}
          />
        </Box>

        {/* Platform Selection */}
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "18px",
              fontWeight: 500,
              mb: 1,
              color: darkMode
                ? getColors.thumbnailTextDark
                : getColors.secondaryTextLight,
            }}
          >
            Select Video Platform
          </Typography>
          <RadioGroup
            row
            value={selectedPlatform}
            onChange={handlePlatformChange}
          >
            <FormControlLabel
              value="YouTube"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={2} p={1.5}>
                  <img
                    src="/assets/youtube-logo.png"
                    alt="YouTube"
                    style={{ width: 200, height: 100, objectFit: "contain" }}
                  />
                </Box>
              }
            />
            {/* <FormControlLabel
              value="Vimeo"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={2} p={1.5}>
                  <img
                    src="/assets/vimeo-logo.png"
                    alt="Vimeo"
                    style={{ width: 200, height: 100, objectFit: "contain" }}
                  />
                </Box>
              }
            /> */}
          </RadioGroup>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "5px",
            fontFamily: "Poppins",
            fontWeight: 500,
            color: darkMode ? getColors.appBarBgLight : getColors.appBarBgDark,
            "&:hover": {
              background: "none",
              color: getColors.primaryMain,
            },
            mr: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          sx={{
            borderRadius: "5px",
            fontFamily: "Poppins",
            fontWeight: 500,
            px: 4,
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
